import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  getAuthToken() {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  async request(endpoint, options = {}) {
    const token = this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'حدث خطأ ما');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth APIs
  async register(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async googleLogin(credential, action = 'login') {
    return this.request('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential, action }),
    });
  }

  async verifyToken() {
    return this.request('/api/auth/verify');
  }

  async forgotPassword(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, password) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Courses APIs
  async getCourses(categoryId = null, search = null) {
    let url = '/api/courses';
    const params = new URLSearchParams();
    if (categoryId) params.append('category', categoryId);
    if (search) params.append('search', search);
    if (params.toString()) url += `?${params.toString()}`;
    return this.request(url);
  }

  async getCourse(id) {
    return this.request(`/api/courses/${id}`);
  }

  // Get PROTECTED content URL with authentication
  // Returns a URL that includes the auth token for secure content access
  getProtectedContentUrl(courseId, type) {
    const token = this.getAuthToken();
    // URL-encode the token to handle special characters like = in base64
    const encodedToken = encodeURIComponent(token);
    return `${this.baseURL}/api/content/${courseId}/${type}?token=${encodedToken}`;
  }

  async getCategories() {
    return this.request('/api/categories');
  }

  // Purchase APIs
  async getUserPurchases() {
    return this.request('/api/purchases/user');
  }

  async getUserManualPayments() {
    return this.request('/api/manual-payments/my-requests');
  }

  async createCheckout(courseId) {
    return this.request('/api/purchases/checkout', {
      method: 'POST',
      body: JSON.stringify({ courseId }),
    });
  }

  async purchaseCourse(courseId) {
    return this.request('/api/purchases', {
      method: 'POST',
      body: JSON.stringify({ courseId }),
    });
  }

  // Manual Payment APIs
  async createManualPaymentRequest(data) {
    return this.request('/api/manual-payments/request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyManualPaymentRequests() {
    return this.request('/api/manual-payments/my-requests');
  }

  // Admin Manual Payment APIs
  async getAdminManualPaymentRequests(status = 'all') {
    const query = status !== 'all' ? `?status=${status}` : '';
    return this.request(`/api/admin/manual-payments${query}`);
  }

  async approveManualPayment(requestId, adminNotes = '') {
    return this.request(`/api/admin/manual-payments/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ adminNotes }),
    });
  }

  async rejectManualPayment(requestId, adminNotes = '') {
    return this.request(`/api/admin/manual-payments/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ adminNotes }),
    });
  }

  // Progress APIs
  async getProgress(courseId) {
    return this.request(`/api/progress/${courseId}`);
  }

  async updateProgress(courseId, currentPage, completed = false) {
    return this.request(`/api/progress/${courseId}`, {
      method: 'PUT',
      body: JSON.stringify({ currentPage, completed }),
    });
  }

  // Admin APIs
  async addCourse(courseData) {
    return this.request('/api/admin/courses', {
      method: 'POST',
      body: JSON.stringify(courseData),
    });
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/api/admin/courses/${courseId}`, {
      method: 'PUT',
      body: JSON.stringify(courseData),
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/api/admin/courses/${courseId}`, {
      method: 'DELETE',
    });
  }

  async getAdminAnalytics() {
    return this.request('/api/admin/analytics');
  }

  async getAdminUserPurchases(userId) {
    return this.request(`/api/admin/user/${userId}/purchases`);
  }

  async deleteUser(userId) {
    return this.request(`/api/admin/user/${userId}`, {
      method: 'DELETE',
    });
  }

  async submitContactForm(formData) {
    return this.request('/api/contact', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  async getContactMessages(status = 'all') {
    return this.request(`/api/admin/contact-messages?status=${status}`);
  }

  async updateContactMessage(messageId, data) {
    return this.request(`/api/admin/contact-messages/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContactMessage(messageId) {
    return this.request(`/api/admin/contact-messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  // Storybooks API
  async getStorybooks(categoryId = null, ageRange = null, searchTerm = '') {
    let url = '/api/storybooks?';
    if (categoryId) url += `category=${categoryId}&`;
    if (ageRange) url += `age=${ageRange}&`;
    if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
    return this.request(url);
  }

  async getStorybookCategories() {
    return this.request('/api/storybook-categories');
  }

  async getStorybook(id) {
    return this.request(`/api/storybooks/${id}`);
  }

  async checkStorybookPurchase(storybookId) {
    return this.request(`/api/storybooks/${storybookId}/check-purchase`);
  }

  // Admin Storybook Management APIs
  async addStorybook(data) {
    return this.request('/api/admin/storybooks', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateStorybook(id, data) {
    return this.request(`/api/admin/storybooks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteStorybook(id) {
    return this.request(`/api/admin/storybooks/${id}`, {
      method: 'DELETE'
    });
  }

  // Subscription APIs
  async createSubscriptionCheckout() {
    return this.request('/api/subscriptions/checkout', {
      method: 'POST',
    });
  }

  // Bundles APIs
  async getUserBundles() {
    return this.request('/api/bundles/my-bundles');
  }

  async createBundleCheckout(courseIds) {
    return this.request('/api/bundles/checkout', {
      method: 'POST',
      body: JSON.stringify({ course_ids: courseIds })
    });
  }

  // Client-side bundle discount calculation for instant feedback
  calculateBundleDiscount(itemCount) {
    const discounts = { 2: 0.10, 3: 0.15, 4: 0.20, 5: 0.25 };
    return discounts[itemCount] || 0;
  }

  calculateBundlePrice(itemCount, pricePerItem = 1500) {
    const total = itemCount * pricePerItem;
    const discount = this.calculateBundleDiscount(itemCount);
    const discountAmount = total * discount;
    const finalPrice = total - discountAmount;
    return {
      original: total,
      discount: discount * 100,
      discountAmount: Math.round(discountAmount),
      final: Math.round(finalPrice)
    };
  }

  // Notifications
  async getNotifications() {
    return this.request('/api/notifications');
  }

  async getUnreadNotificationsCount() {
    return this.request('/api/notifications/unread-count');
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/api/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/api/notifications/mark-all-read', {
      method: 'PUT'
    });
  }

  async deleteNotification(notificationId) {
    return this.request(`/api/notifications/${notificationId}`, {
      method: 'DELETE'
    });
  }

  // Newsletter
  async subscribeToNewsletter(email) {
    return this.request('/api/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async getNewsletterSubscribers() {
    return this.request('/api/admin/newsletter/subscribers');
  }

  async deleteNewsletterSubscriber(id) {
    return this.request(`/api/admin/newsletter/subscribers/${id}`, {
      method: 'DELETE'
    });
  }

  // Subscription Management
  async getSubscriptionStatus() {
    return this.request('/api/subscriptions/status');
  }

  async renewSubscription() {
    return this.request('/api/subscriptions/renew', {
      method: 'POST'
    });
  }
}

export default new ApiService();
